import {useNavigate} from 'react-router-dom';
import {
	ExamBookingEnum,
	HealthBookingEnum,
	QueryParamsForTaggingEnum,
} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams, useMediaQueries} from 'presentation/pages';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {getUserInfoFromToken} from 'presentation/utils';
import {UseLocalitiesListProps} from './types';
import {BookingFlowType, FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {LocationType} from 'presentation/pages/private/store/locationStore/types';
import {useNotFound} from 'presentation/pages/private/hooks/useNotFound';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useGetLocalitiesQuery} from 'presentation/pages/private/booking/queries/useGetLocalitiesQuery';
import {TypeLocalNameEnum} from 'domain/usecases';
import {getBookingTypeAndOrigin} from 'presentation/modules/booking/shared/utils/getBookingTypeAndOrigin';

export function useLocalitiesList({flowType}: UseLocalitiesListProps) {
	const navigate = useNavigate();
	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const setLocation = useLocationStore(state => state.setLocation);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const {currentEmail, currentPhone} = usePersonalData();
	const {SEARCH_PARAMS, clearSearch, setSearchParams} = useQueryParams();
	const {ufCode, ufName, query} = SEARCH_PARAMS;
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {handleCallChatRoom, handleGuideNav, notFoundModalIsOpen, setNotFoundModalIsOpen} =
		useNotFound();

	const {
		refetch,
		isFetching: loadingList,
		data: localitiesList,
		error: localitiesListError,
	} = useGetLocalitiesQuery({flowType, stateCode: ufCode});

	const isExamFlow = flowType === FlowEnum.EXAM;

	const bookingFlowPath = isExamFlow
		? ExamBookingEnum.EXAM_BOOKING_FLOW
		: HealthBookingEnum.HEALTH_BOOKING_FLOW;

	const {SPECIALITY_OR_EXAM_STEP} = getStepPosition({
		flowType,
		isManualLocation: bookingData?.isManualLocation,
	});

	const NOT_FOUND = !localitiesList || localitiesList.length === 0;

	const filteredLocalityList = localitiesList?.filter(localityItem =>
		localityItem?.nmLocal?.toLowerCase().includes(query?.trim().toLowerCase()),
	);
	const isListEmpty = filteredLocalityList?.length === 0;

	const handleConfirmSelection = ({
		nmLocal,
		nmTipoLocal,
		nmBairro,
	}: {
		nmLocal: string;
		nmTipoLocal: TypeLocalNameEnum;
		nmBairro: string;
	}) => {
		const locationType = getLocationTypeFromFlow();
		const {bookingType, bookingOrigin} = getBookingTypeAndOrigin(flowType as BookingFlowType);

		setLocation(cdPessoaC, locationType, {
			state: {
				cdUf: ufCode,
				nmUf: ufName,
			},
			city: nmLocal,
			typeLocalName: nmTipoLocal,
			reference: nmBairro,
		});
		clearSearch();

		setBookingData(prev => ({
			...prev,
			email: currentEmail,
			phone: currentPhone,
			ufCode,
			ufName,
			cityName: nmLocal,
			typeLocalName: nmTipoLocal,
			reference: nmBairro,
			bookingType,
			bookingOrigin,
		}));

		setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);

		navigate(bookingFlowPath, {
			replace: true,
		});

		setSearchParams(prev => {
			prev.set(QueryParamsForTaggingEnum.GEOLOCATION, 'false');
			return prev;
		});
	};

	const getLocationTypeFromFlow = (): LocationType =>
		isExamFlow ? FlowType.EXAM : FlowType.DEFAULT;

	return {
		loadingList,
		localitiesList: filteredLocalityList,
		localitiesListError,
		handleConfirmSelection,
		notFoundModalIsOpen,
		setNotFoundModalIsOpen,
		handleCallChatRoom,
		handleGuideNav,
		isListEmpty,
		NOT_FOUND,
		refetch,
		isMobile,
	};
}
